'use client';
import React, { useEffect, useRef, useState } from 'react';

const HEADLINE = "Every Airtable trick worth knowing. Indexed.";

export default function HeroSection() {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const indexRef = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => {
      if (indexRef.current < HEADLINE.length) {
        setDisplayed(HEADLINE.slice(0, indexRef.current + 1));
        indexRef.current++;
      } else {
        setDone(true);
        clearInterval(timer);
      }
    }, 42);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-24 pb-16 animated-gradient noise-overlay">
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="blob-drift absolute rounded-full"
          style={{
            width: 600, height: 600,
            top: '10%', left: '-10%',
            background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div
          className="blob-drift-2 absolute rounded-full"
          style={{
            width: 500, height: 500,
            top: '30%', right: '-5%',
            background: 'radial-gradient(circle, rgba(30,27,75,0.5) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 300, height: 300,
            bottom: '15%', left: '40%',
            background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)',
            filter: 'blur(30px)',
          }}
        />
      </div>

      {/* Headline */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-6 mb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6 font-mono text-xs"
          style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', color: '#C4B5FD' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
          Developer Knowledge Base · v2.0
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6"
          style={{ color: '#F4F4F5' }}>
          <span className={!done ? 'typewriter-cursor' : ''}>{displayed}</span>
        </h1>

        <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{ color: '#A1A1AA' }}>
          Scripting patterns, automation flows, schema architectures, and extension hacks —
          built by developers who ship Airtable for a living.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <a href="#explorer"
            className="px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200"
            style={{ background: '#7C3AED', color: 'white', boxShadow: '0 0 20px rgba(124,58,237,0.4)' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(124,58,237,0.6)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(124,58,237,0.4)'}
          >
            Browse Articles
          </a>
          <a href="#download"
            className="px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200"
            style={{ background: 'transparent', border: '1px solid rgba(124,58,237,0.35)', color: '#C4B5FD' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(124,58,237,0.1)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(124,58,237,0.6)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(124,58,237,0.35)'; }}
          >
            Download Reader →
          </a>
        </div>
      </div>

      {/* Isometric product screenshot */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6">
        <div className="isometric-tilt violet-glow rounded-2xl overflow-hidden"
          style={{ background: '#0F0E1A', border: '1px solid rgba(124,58,237,0.2)' }}>
          {/* Screenshot header bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'rgba(124,58,237,0.12)', background: 'rgba(30,27,75,0.4)' }}>
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ background: '#ef4444' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#f59e0b' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#22c55e' }} />
            </div>
            <div className="flex-1 mx-4">
              <div className="h-5 rounded-md flex items-center px-3 font-mono text-xs" style={{ background: 'rgba(0,0,0,0.3)', color: '#52525B' }}>
                basecamp.dev/explore
              </div>
            </div>
          </div>

          {/* Screenshot content: filter bar + card grid */}
          <div className="p-6">
            {/* Filter chips row */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {['All', 'Scripting', 'Automations', 'Integrations', 'Schema'].map((f, i) => (
                <span key={f} className="px-3 py-1 rounded-full text-xs font-mono font-medium"
                  style={{
                    background: i === 0 ? 'rgba(124,58,237,0.25)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${i === 0 ? 'rgba(124,58,237,0.6)' : 'rgba(255,255,255,0.08)'}`,
                    color: i === 0 ? '#C4B5FD' : '#71717A',
                  }}>
                  {f}
                </span>
              ))}
            </div>

            {/* Mini card grid */}
            <div className="grid grid-cols-3 gap-4">
              {screenshotCards.map((card, i) => (
                <div key={i}
                  className="rounded-xl p-4 transition-all"
                  style={{
                    background: i === 0 ? 'rgba(124,58,237,0.12)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${i === 0 ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                  {i === 0 ? (
                    // Expanded card with code snippet
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <span className="tag-pill">{card.category}</span>
                        <span className="tag-pill difficulty-advanced">Advanced</span>
                      </div>
                      <p className="text-xs font-semibold mb-2 leading-snug" style={{ color: '#F4F4F5' }}>{card.title}</p>
                      {/* Mini code block */}
                      <div className="rounded-lg p-2 font-mono text-[9px] leading-relaxed" style={{ background: 'rgba(0,0,0,0.5)', color: '#C4B5FD' }}>
                        <span style={{ color: '#8B5CF6' }}>const</span>{' '}
                        <span style={{ color: '#F4F4F5' }}>records</span>{' = '}
                        <span style={{ color: '#34d399' }}>await</span>{' table.'}<br />
                        {'  '}<span style={{ color: '#F4F4F5' }}>selectRecordsAsync</span>{'({'}
                        <br />{'  fields: ['}
                        <span style={{ color: '#fbbf24' }}>&apos;Status&apos;</span>{']'}<br />
                        {'});'}
                      </div>
                      {/* Schema thumbnail */}
                      <div className="mt-2 rounded p-2 flex gap-1" style={{ background: 'rgba(30,27,75,0.6)' }}>
                        {['Name', 'Status', 'Link'].map(f => (
                          <span key={f} className="text-[8px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(124,58,237,0.2)', color: '#C4B5FD' }}>{f}</span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <span className="tag-pill mb-2 inline-block">{card.category}</span>
                      <p className="text-xs font-medium leading-snug mt-2" style={{ color: '#A1A1AA' }}>{card.title}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-[9px] font-mono" style={{ color: '#52525B' }}>{card.readTime}</span>
                        <span className="text-[9px] font-mono" style={{ color: '#52525B' }}>· {card.difficulty}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 mt-12 flex flex-col items-center gap-2">
        <span className="text-xs font-mono" style={{ color: '#3F3F46' }}>scroll to explore</span>
        <div className="w-px h-8 animate-pulse" style={{ background: 'linear-gradient(to bottom, #7C3AED, transparent)' }} />
      </div>
    </section>
  );
}

const screenshotCards = [
  { category: 'Scripting', title: 'Batch dedup with selectRecordsAsync — the right way', readTime: '8 min', difficulty: 'Advanced' },
  { category: 'Automations', title: 'Chaining webhook triggers without hitting rate limits', readTime: '5 min', difficulty: 'Intermediate' },
  { category: 'Schema', title: 'Linked record patterns for client-facing bases', readTime: '6 min', difficulty: 'Beginner' },
];